/**
 * Post action hooks — like, create post, delete post, repost, report.
 * Optimistic updates target only feed list queries.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  likePost, unlikePost,
  createPost, deletePost,
  reportPost, createRepost,
} from '@turnifly/shared/api'
import type { CreatePostParams } from '@turnifly/shared/api'
import type { FeedItem } from '@turnifly/shared/types'
import type { InfiniteData } from '@tanstack/react-query'
import { feedKeys } from './use-feed'

const FEED_FOR_YOU_PREFIX = ['feed', 'for-you'] as const
const FEED_FOLLOWING_PREFIX = ['feed', 'following'] as const

function updateFeedPages(
  old: InfiniteData<FeedItem[]> | undefined,
  postId: string,
  updater: (item: FeedItem) => FeedItem,
): InfiniteData<FeedItem[]> | undefined {
  if (!old?.pages) return old
  return {
    ...old,
    pages: old.pages.map((page) =>
      page.map((item) => item.post_id === postId ? updater(item) : item),
    ),
    pageParams: old.pageParams,
  }
}

// ── LIKE / UNLIKE ──────────────────────────────────────────

export function useToggleLike() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ postId, userId, isLiked }: {
      postId: string; userId: string; isLiked: boolean
    }) => {
      if (isLiked) {
        await unlikePost(postId, userId)
      } else {
        await likePost(postId, userId)
      }
    },
    onMutate: async ({ postId, isLiked, userId }) => {
      await queryClient.cancelQueries({ queryKey: feedKeys.forYou(userId) })
      await queryClient.cancelQueries({ queryKey: feedKeys.following(userId) })

      const updater = (item: FeedItem): FeedItem => ({
        ...item,
        is_liked_by_me: !isLiked,
        likes_count: item.likes_count + (isLiked ? -1 : 1),
      })

      queryClient.setQueriesData<InfiniteData<FeedItem[]>>(
        { queryKey: FEED_FOR_YOU_PREFIX },
        (old) => updateFeedPages(old, postId, updater),
      )
      queryClient.setQueriesData<InfiniteData<FeedItem[]>>(
        { queryKey: FEED_FOLLOWING_PREFIX },
        (old) => updateFeedPages(old, postId, updater),
      )
      // Also update profile feed
      queryClient.setQueriesData<InfiniteData<FeedItem[]>>(
        { queryKey: ['profile-feed'] },
        (old) => updateFeedPages(old, postId, updater),
      )
    },
    onError: (_err, variables) => {
      void queryClient.invalidateQueries({ queryKey: feedKeys.forYou(variables.userId) })
      void queryClient.invalidateQueries({ queryKey: feedKeys.following(variables.userId) })
    },
  })
}

// ── CREATE POST ────────────────────────────────────────────

export function useCreatePost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreatePostParams) => createPost(params),
    onSuccess: (_data, params) => {
      // Invalidate following feed (shows own posts) + profile feed
      void queryClient.invalidateQueries({ queryKey: feedKeys.following(params.userId) })
      void queryClient.invalidateQueries({ queryKey: ['profile-feed', params.userId] })
    },
  })
}

// ── DELETE POST ────────────────────────────────────────────

export function useDeletePost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (postId: string) => deletePost(postId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEED_FOR_YOU_PREFIX })
      void queryClient.invalidateQueries({ queryKey: FEED_FOLLOWING_PREFIX })
      void queryClient.invalidateQueries({ queryKey: ['profile-feed'] })
    },
  })
}

// ── REPOST ────────────────────────────────────────────────

export function useRepost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, originalPostId, caption }: {
      userId: string; originalPostId: string; caption?: string
    }) => createRepost(userId, originalPostId, caption),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEED_FOR_YOU_PREFIX })
      void queryClient.invalidateQueries({ queryKey: FEED_FOLLOWING_PREFIX })
      void queryClient.invalidateQueries({ queryKey: ['profile-feed'] })
    },
  })
}

// ── REPORT POST ────────────────────────────────────────────

export function useReportPost() {
  return useMutation({
    mutationFn: ({ postId, userId, reason, description }: {
      postId: string; userId: string; reason: string; description?: string
    }) => reportPost(postId, userId, reason, description),
  })
}
